import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';
import i18n from '../../../../i18n';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_MATERIALS } from '../../../../gql/queries/actions/materials';

// Utils
import { AuthContextType } from '../../../../hooks/useAuth';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';

// Constants
import { formMaterialManagementNaming } from '../../../../components/forms/AddMaterialManagementForm/constants';
import { FETCH_MEASURE_TYPES } from '../../../../gql/queries/actions/measureTypes';
import { FETCH_CODIFICATIONS } from '../../../../gql/queries/actions/codification';
import { FETCH_SUBCODIFICATIONS } from '../../../../gql/queries/actions/subCodification';
import { FETCH_TEAMS } from '../../../../gql/queries/users';

const loadTableData = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      let materialsToReturn;
      const {
        data: { materials },
      } = await client.query({
        query: FETCH_MATERIALS,
        fetchPolicy: 'cache-first',
      });
      if (!materials || materials.length === 0) {
        const {
          data: { materials },
        } = await client.query({
          query: FETCH_MATERIALS,
        });
        materialsToReturn = materials;
      } else {
        materialsToReturn = materials;
      }
      const [
        {
          data: { measureTypes },
        },
        {
          data: { codifications },
        },
        {
          data: { subCodifications },
        },
        {
          data: { teams },
        },
      ] = await Promise.all([
        client.query({
          query: FETCH_MEASURE_TYPES,
        }),
        client.query({
          query: FETCH_CODIFICATIONS,
        }),
        client.query({
          query: FETCH_SUBCODIFICATIONS,
        }),
        client.query({
          query: FETCH_TEAMS,
        }),
      ]);

      materialsToReturn = [...materialsToReturn].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      // Mapas de resolución O(1). Antes esto eran 3 queries POR MATERIAL
      // (~600 con el catálogo actual); el loader hermano de NewAction ya
      // usaba mapas y este se había quedado atrás.
      const measureTypeMap = new Map<string, string>(
        measureTypes.map((m: { id: string; name: string }) => [m.id, m.name])
      );
      const codificationMap = new Map<string, string>(
        codifications.map((c: { id: string; name: string }) => [c.id, c.name])
      );
      const subCodificationMap = new Map<string, string>(
        subCodifications.map((s: { id: string; name: string }) => [
          s.id,
          s.name,
        ])
      );
      const teamMap = new Map<string, string>(
        (teams ?? []).map((team: { id: string; name: string }) => [
          team.id,
          team.name,
        ])
      );

      const materialsData = materialsToReturn.map(
        (material: {
          id: string;
          materialId: string;
          name: string;
          typeOfMeasurement: string;
          codification: string;
          subCodification: string;
          measureTypeIDs: string[];
          codificationIDs: string[];
          subCodificationIDs: string[];
          teamIDs?: string[];
          isActive?: boolean | null;
        }) => ({
          id: material.id,
          materialId: material.materialId,
          name: material.name,
          typeOfMeasurement:
            measureTypeMap.get(material?.measureTypeIDs?.[0]) || '',
          codification: codificationMap.get(material?.codificationIDs?.[0]) || '',
          subCodification:
            subCodificationMap.get(material?.subCodificationIDs?.[0]) || '',
          teamIDs: material.teamIDs ?? [],
          // 🔴 AUSENTE o null = ACTIVO. El backend lo deja nulo a propósito
          // para no necesitar backfill; leerlo como «false» vaciaría la tabla
          // de activos el día del despliegue.
          isActive: material.isActive !== false,
          // Solo los equipos DEL TENANT tienen nombre resoluble: un material
          // global puede arrastrar equipos de otros tenants, que aquí no se
          // muestran (ni deben) — misma intersección que hace el filtro.
          teamNames: (material.teamIDs ?? [])
            .map((teamId) => teamMap.get(teamId))
            .filter((name): name is string => Boolean(name)),
        })
      );
      // Dos tablas en fila (activos | inactivos), layout de Empresas externas.
      // `materialsData` se conserva entero: lo consumen el formulario de alta
      // y el modal de equipos, que no distinguen por estado.
      const activeMaterials = materialsData.filter(
        (material: { isActive: boolean }) => material.isActive
      );
      const inactiveMaterials = materialsData.filter(
        (material: { isActive: boolean }) => !material.isActive
      );

      return {
        materialsData,
        activeMaterials,
        inactiveMaterials,
        measureTypes,
        codifications,
        subCodifications,
        teams: teams ?? [],
      };
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('[gestión] error cargando los datos de materiales', err);
    return true;
  }
};

export const MaterialsManagementLoader: (
  materialData: AuthContextType | null
) => LoaderFunction | undefined = (materialData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const data = await loadTableData(materialData);

  let cacheData =
    sessionStorage.getItem(
      formMaterialManagementNaming.NEW_MATERIAL_MANAGEMENT_FORM
    ) || {};

  if (Object.keys(cacheData).length > 0) {
    cacheData = JSON.parse(decompressSessionStorage(cacheData as string));
  }
  return {
      data,
      cacheData,
    };
};

  return { data: load() };
};
